import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Shell, Card, Steps } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { apiError, http, SEDES, SEDE_DAYS_TEXT, ANAMNESIS_FIELDS as API_ANAMNESIS_FIELDS } from "../lib/api";

const ANAMNESIS_FIELDS = API_ANAMNESIS_FIELDS;

export default function LogoAlta() {
  const { sede } = useParams();
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const logged = user && user.role === "family";
  const total = logged ? 3 : 4;
  const [step, setStep] = useState(1);
  const [patient, setPatient] = useState({ name: "", bono_sessions: 4 });
  const [family, setFamily] = useState({ family_name: "", email: "", phone: "", password: "" });
  const [accepted, setAccepted] = useState(false);
  const [anam, setAnam] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [bonoPrices, setBonoPrices] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [chosenDay, setChosenDay] = useState(null);
  const [hours, setHours] = useState({});

  useEffect(() => {
    http.get("/logopedia/weekly-availability", { params: { sede } })
      .then(({ data }) => setWeekly(data))
      .catch(() => setWeekly(null));
  }, [sede]);

  const fixedSchedule = Number(patient.bono_sessions) === 4
    ? (chosenDay && hours[chosenDay] ? [{ day: chosenDay, start: hours[chosenDay] }] : [])
    : (weekly?.days || []).filter((d) => hours[d.day]).map((d) => ({ day: d.day, start: hours[d.day] }));
  const scheduleReady = fixedSchedule.length === (Number(patient.bono_sessions) === 4 ? 1 : 2);

  useEffect(() => {
    http.get("/logopedia/bono-prices").then(({ data }) => setBonoPrices(data.prices));
  }, []);

  const anamStep = logged ? 2 : 3;
  const confirmStep = logged ? 3 : 4;
  const labels = logged
    ? ["Datos del paciente", "Anamnesis", "Confirmación"]
    : ["Datos del paciente", "Datos de la familia", "Anamnesis", "Confirmación"];

  const finish = async () => {
    setError("");
    setBusy(true);
    try {
      if (!logged) await register({ ...family, accepted_privacy: true });
      const { data } = await http.post("/logopedia/patients", {
        name: patient.name, sede, bono_sessions: Number(patient.bono_sessions),
        schedule_mode: "fijo", fixed_schedule: fixedSchedule,
      });
      await http.post("/logopedia/anamnesis", { patient_id: data.id, data: anam });
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <Shell accent="logo" title="Registro completado" testid="alta-done">
        <Card className="max-w-2xl">
          <p className="text-lg">
            <strong>{patient.name}</strong> queda registrado/a en la sede de {SEDES[sede]} con un
            bono de {patient.bono_sessions} sesiones y su horario fijo semanal ya reservado. Puede
            consultar sus próximas sesiones desde su cuenta.
          </p>
          <Button data-testid="alta-go-profile-btn" onClick={() => navigate("/perfil/logopedia")}
            className="mt-6 h-14 w-full bg-logo text-lg hover:bg-logo-hover">
            Acceder a mi área de Logopedia
          </Button>
        </Card>
      </Shell>
    );

  return (
    <Shell accent="logo" title={`Registro en Logopedia — ${SEDES[sede] || sede}`} testid="logo-alta-page">
      <Card className="max-w-3xl">
        <Steps step={step} total={total} label={labels[step - 1]} />

        {step === 1 && (
          <div className="space-y-6">
            <div>
              <Label htmlFor="pname" className="text-lg">Nombre y apellidos del paciente</Label>
              <Input id="pname" data-testid="patient-name-input" className="mt-2 h-14 text-lg"
                value={patient.name} onChange={(e) => setPatient({ ...patient, name: e.target.value })} />
            </div>
            <div>
              <p className="text-lg font-medium">Bono de sesiones</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {[4, 8].map((n) => {
                  const p = (bonoPrices || []).find((x) => x.sessions === n);
                  return (
                  <button key={n} type="button" data-testid={`bono-${n}-btn`}
                    onClick={() => setPatient({ ...patient, bono_sessions: n })}
                    className={`rounded-xl border-2 p-5 text-left text-lg transition-colors ${
                      Number(patient.bono_sessions) === n ? "border-logo bg-logo-light" : "border-border bg-white hover:border-logo"}`}>
                    <span className="block font-medium">Bono de {n} sesiones</span>
                    {p && (
                      <span className="mt-1 block text-base" data-testid={`bono-price-${n}`}>
                        {p.offer ? (
                          <>
                            <span className="text-ink-700 line-through">{p.regular} €</span>{" "}
                            <strong className="text-logo-text">OFERTA: {p.price} €</strong>
                            <span className="block text-sm text-ink-700">
                              Hasta el {p.offer_until.split("-").reverse().join("/")}
                            </span>
                          </>
                        ) : <strong>{p.price} €</strong>}
                      </span>
                    )}
                  </button>
                  );
                })}
              </div>
              <p className="mt-3 text-base text-ink-700">
                El bono se abona en el centro, no a través de la web; en esta plataforma únicamente
                se realiza el seguimiento de las sesiones.
              </p>
            </div>
            <div>
              <p className="text-lg font-medium">Horario fijo semanal</p>
              <p className="mt-1 text-base text-ink-700">
                En la sede de {SEDES[sede]} la atención es los {SEDE_DAYS_TEXT[sede]}. El bono de 4
                sesiones corresponde a <strong>un día a la semana</strong> y el de 8 sesiones a{" "}
                <strong>los dos días</strong>. La franja elegida queda reservada todas las semanas;
                si más adelante necesita cambiarla de forma definitiva, el equipo de SISU la
                modificará por usted.
              </p>
              {Number(patient.bono_sessions) === 4 && (
                <div className="mt-4">
                  <p className="text-base font-medium">1. Elija el día</p>
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    {(weekly?.days || []).map((d) => (
                      <button key={d.day} type="button" data-testid={`fixed-day-${d.day}-btn`}
                        onClick={() => setChosenDay(d.day)}
                        className={`rounded-xl border-2 p-4 text-left text-lg capitalize transition-colors ${
                          chosenDay === d.day ? "border-logo bg-logo-light" : "border-border bg-white hover:border-logo"}`}>
                        {d.day_name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-4">
                <p className="text-base font-medium">
                  {Number(patient.bono_sessions) === 4 ? "2. Elija la hora" : "Elija la hora de cada día"}
                </p>
                {(weekly?.days || [])
                  .filter((d) => Number(patient.bono_sessions) === 8 || d.day === chosenDay)
                  .map((d) => (
                  <div key={d.day} className="mt-3">
                    <p className="text-base capitalize text-ink-700">{d.day_name}</p>
                    <div className="mt-2 grid gap-2 sm:grid-cols-3">
                      {d.times.map((t) => (
                        <button key={t.start} type="button" disabled={!t.available}
                          data-testid={`fixed-time-${d.day}-${t.start}`}
                          onClick={() => setHours({ ...hours, [d.day]: t.start })}
                          className={`rounded-xl border-2 p-3 text-base transition-colors ${
                            hours[d.day] === t.start ? "border-logo bg-logo-light font-semibold"
                              : !t.available ? "cursor-not-allowed border-border bg-muted text-ink-700 opacity-70"
                                : "border-border bg-white hover:border-logo"}`}>
                          {t.start}{!t.available && " · ocupada"}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <Button data-testid="alta-step1-next-btn" disabled={!patient.name || !scheduleReady}
              className="h-14 w-full bg-logo text-lg hover:bg-logo-hover" onClick={() => setStep(2)}>
              Siguiente paso
            </Button>
          </div>
        )}

        {step === 2 && !logged && (
          <div className="space-y-6">
            <div>
              <Label htmlFor="lname" className="text-lg">Nombre del padre, madre o tutor legal</Label>
              <Input id="lname" data-testid="logo-family-name-input" className="mt-2 h-14 text-lg"
                value={family.family_name} onChange={(e) => setFamily({ ...family, family_name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="lphone" className="text-lg">Teléfono móvil para los recordatorios por WhatsApp</Label>
              <Input id="lphone" data-testid="logo-family-phone-input" className="mt-2 h-14 text-lg"
                value={family.phone} onChange={(e) => setFamily({ ...family, phone: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="lemail" className="text-lg">Correo electrónico (será el usuario de acceso)</Label>
              <Input id="lemail" data-testid="logo-family-email-input" type="email" className="mt-2 h-14 text-lg"
                value={family.email} onChange={(e) => setFamily({ ...family, email: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="lpass" className="text-lg">Contraseña (mínimo 6 caracteres)</Label>
              <Input id="lpass" data-testid="logo-family-password-input" type="password" className="mt-2 h-14 text-lg"
                value={family.password} onChange={(e) => setFamily({ ...family, password: e.target.value })} />
            </div>
            <div className="flex items-start gap-3 rounded-xl bg-cream p-4">
              <Checkbox id="lprivacy" data-testid="logo-privacy-checkbox" checked={accepted}
                onCheckedChange={(v) => setAccepted(!!v)} className="mt-1 h-6 w-6" />
              <Label htmlFor="lprivacy" className="text-base leading-relaxed">
                Como padre, madre o tutor legal, presto mi consentimiento para el tratamiento de
                estos datos, incluidos los datos de salud recogidos en la anamnesis, y acepto la{" "}
                <Link to="/privacidad" className="underline" target="_blank">Política de Privacidad</Link>.
              </Label>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="h-14 flex-1 text-lg" onClick={() => setStep(1)}>Volver atrás</Button>
              <Button data-testid="alta-step2-next-btn" className="h-14 flex-1 bg-logo text-lg hover:bg-logo-hover"
                disabled={!family.family_name || !family.email || !family.phone || family.password.length < 6 || !accepted}
                onClick={() => setStep(3)}>Siguiente paso</Button>
            </div>
          </div>
        )}

        {step === anamStep && (
          <div className="space-y-6">
            <p className="text-lg text-ink-700">
              Esta información nos permite preparar la primera sesión. Solo es accesible para el
              equipo de SISU. Puede dejar en blanco los apartados que no pueda completar.
            </p>
            {ANAMNESIS_FIELDS.map(([key, label]) => (
              <div key={key}>
                <Label htmlFor={key} className="text-lg">{label}</Label>
                <Textarea id={key} data-testid={`anamnesis-${key}-input`} rows={3}
                  className="mt-2 text-lg" value={anam[key] || ""}
                  onChange={(e) => setAnam({ ...anam, [key]: e.target.value })} />
              </div>
            ))}
            <div className="flex gap-3">
              <Button variant="outline" className="h-14 flex-1 text-lg" onClick={() => setStep(logged ? 1 : 2)}>Volver atrás</Button>
              <Button data-testid="anamnesis-next-btn" className="h-14 flex-1 bg-logo text-lg hover:bg-logo-hover"
                onClick={() => setStep(confirmStep)}>Siguiente paso</Button>
            </div>
          </div>
        )}

        {step === confirmStep && (
          <div className="space-y-6">
            <h2 className="font-head text-2xl font-medium">Revisión y confirmación</h2>
            <div className="rounded-xl bg-cream p-5 text-lg">
              <p><strong>Paciente:</strong> {patient.name}</p>
              <p><strong>Sede:</strong> {SEDES[sede]}</p>
              <p><strong>Bono:</strong> {patient.bono_sessions} sesiones
                {" · "}
                <span data-testid="confirm-bono-price">
                  {((bonoPrices || []).find((x) => x.sessions === Number(patient.bono_sessions))?.price ?? "—")} €
                </span>
              </p>
              <p><strong>Modalidad de horario:</strong>{" "}
                Horario fijo semanal</p>
              <p data-testid="confirm-fixed-schedule"><strong>Horario elegido:</strong>{" "}
                {fixedSchedule.length
                  ? fixedSchedule.map((f) => {
                      const d = (weekly?.days || []).find((x) => x.day === f.day);
                      return `${d ? d.day_name : ""} a las ${f.start}`;
                    }).join(" · ")
                  : "sin elegir"}</p>
            </div>
            {error && <p data-testid="alta-error" className="rounded-lg bg-red-50 p-4 text-base text-red-700">{error}</p>}
            <div className="flex gap-3">
              <Button variant="outline" className="h-14 flex-1 text-lg" onClick={() => setStep(anamStep)}>Volver atrás</Button>
              <Button data-testid="confirm-alta-btn" disabled={busy}
                className="h-14 flex-1 bg-logo text-lg hover:bg-logo-hover" onClick={finish}>
                {busy ? "Guardando..." : "Confirmar el registro"}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </Shell>
  );
}
